import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDeviceBrowserPolicies } from "@/hooks/useDeviceBrowserPolicies";
import { useDeviceBrowserMode } from "@/hooks/useDeviceBrowserMode";
import { useBlueJayRulesRead } from "@/hooks/useBlueJayRulesRead";
import { Settings, Grid3X3, Shield, ShieldOff } from "lucide-react";
import { DeviceSidebar } from "./DeviceSidebar";
import { DeviceBrowserCategoriesContent } from "./DeviceBrowserCategoriesContent";
import { DeviceBrowserWhitelistContent } from "./DeviceBrowserWhitelistContent";
import { DeviceBrowserBlacklistContent } from "./DeviceBrowserBlacklistContent";
import { DeviceBrowserSettingsContent } from "./DeviceBrowserSettingsContent";
import { ConvexErrorBoundary } from "./ConvexErrorBoundary";
import { PolicyDebugCard } from "./PolicyDebugCard";
import { type NsfwPresetLevel, type AccuracyLevel, DEFAULT_BLUEJAY_CONFIG } from "@/config/nsfwRulePresets";
import type { BrowserMode } from "@/types/convex";

interface DeviceBrowserTabProps {
  deviceId: string;
}

export const DeviceBrowserTab = ({ deviceId }: DeviceBrowserTabProps) => {
  const [searchParams] = useSearchParams();
  const showDebug = searchParams.get("debugPolicy") === "1";
  
  const [activeSection, setActiveSection] = useState("settings");
  const [nsfwLevel, setNsfwLevel] = useState<NsfwPresetLevel>(DEFAULT_BLUEJAY_CONFIG.nsfwLevel);
  const [human, setHuman] = useState<boolean>(DEFAULT_BLUEJAY_CONFIG.human);
  const [accuracy, setAccuracy] = useState<AccuracyLevel>(DEFAULT_BLUEJAY_CONFIG.accuracy);
  
  // Use the new mode hook for explicit mode management
  const { browserMode, setBrowserMode, isLoading: isModeLoading, refetch } = useDeviceBrowserMode(deviceId);
  
  // Get device browser policies for updating settings (not mode)
  const { updateBrowserPolicy } = useDeviceBrowserPolicies(deviceId);
  
  // Read blueJayRules from external endpoint for config values
  const { blueJayRules, isLoading: isLoadingRules } = useBlueJayRulesRead(deviceId);

  // Initialize config state from blueJayRules
  useEffect(() => {
    if (isLoadingRules || !blueJayRules) return;
    
    setNsfwLevel(blueJayRules.config?.nsfwLevel ?? DEFAULT_BLUEJAY_CONFIG.nsfwLevel);
    setHuman(blueJayRules.config?.human ?? DEFAULT_BLUEJAY_CONFIG.human);
    setAccuracy(blueJayRules.config?.accuracy ?? DEFAULT_BLUEJAY_CONFIG.accuracy);
  }, [blueJayRules, isLoadingRules]);

  /**
   * Filter sidebar items based on explicit browserMode:
   * - none: Settings only
   * - whitelist: Settings, Whitelist only
   * - categories: All sections
   */
  const sidebarItems = [
    { id: "settings", label: "Settings", icon: Settings },
    { id: "categories", label: "Categories", icon: Grid3X3 },
    { id: "whitelist", label: "White List", icon: Shield },
    { id: "blacklist", label: "Black List", icon: ShieldOff },
  ];

  const filteredSidebarItems = 
    browserMode === "none" 
      ? sidebarItems.filter(item => item.id === "settings")
      : browserMode === "whitelist"
      ? sidebarItems.filter(item => ["settings", "whitelist"].includes(item.id))
      : sidebarItems; // categories mode - show all

  // Handle browser mode change - uses the new hook
  const handleBrowserModeChange = async (mode: BrowserMode) => {
    await setBrowserMode(mode);
  };

  // Switch to appropriate section if current section is not available for the mode
  useEffect(() => {
    const availableIds = filteredSidebarItems.map(item => item.id);
    if (!availableIds.includes(activeSection)) {
      setActiveSection(filteredSidebarItems[0]?.id || "settings");
    }
  }, [browserMode, activeSection, filteredSidebarItems]);

  const renderContent = () => {
    const hasRules = blueJayRules !== null && blueJayRules !== false;
    const currentLists = hasRules ? {
      categories: blueJayRules.categories ?? [],
      allowlist: blueJayRules.allowlist ?? [],
      blocklist: blueJayRules.blocklist ?? [],
    } : { categories: [], allowlist: [], blocklist: [] };

    switch (activeSection) {
      case "settings":
        return (
          <DeviceBrowserSettingsContent
            deviceId={deviceId}
            browserMode={browserMode === "categories" ? "filtered" : browserMode}
            onBrowserModeChange={(mode) => {
              // Map "filtered" back to "categories" for the hook
              const mappedMode: BrowserMode = mode === "filtered" ? "categories" : mode;
              handleBrowserModeChange(mappedMode);
            }}
            nsfwLevel={nsfwLevel}
            onNsfwLevelChange={(level) => {
              setNsfwLevel(level);
              updateBrowserPolicy({ nsfwLevel: level }, { ...currentLists, nsfwLevel, human, accuracy }, refetch);
            }}
            human={human}
            onHumanChange={(value) => {
              setHuman(value);
              updateBrowserPolicy({ human: value }, { ...currentLists, nsfwLevel, human, accuracy }, refetch);
            }}
            accuracy={accuracy}
            onAccuracyChange={(value) => {
              setAccuracy(value);
              updateBrowserPolicy({ accuracy: value }, { ...currentLists, nsfwLevel, human, accuracy }, refetch);
            }}
          />
        );
      case "categories":
        return <DeviceBrowserCategoriesContent deviceId={deviceId} />;
      case "whitelist":
        return <DeviceBrowserWhitelistContent deviceId={deviceId} />;
      case "blacklist":
        return <DeviceBrowserBlacklistContent deviceId={deviceId} />;
      default:
        return (
          <DeviceBrowserSettingsContent
            deviceId={deviceId}
            browserMode={browserMode === "categories" ? "filtered" : browserMode}
            onBrowserModeChange={(mode) => {
              const mappedMode: BrowserMode = mode === "filtered" ? "categories" : mode;
              handleBrowserModeChange(mappedMode);
            }}
            nsfwLevel={nsfwLevel}
            onNsfwLevelChange={(level) => {
              setNsfwLevel(level);
              updateBrowserPolicy({ nsfwLevel: level }, { ...currentLists, nsfwLevel, human, accuracy }, refetch);
            }}
            human={human}
            onHumanChange={(value) => {
              setHuman(value);
              updateBrowserPolicy({ human: value }, { ...currentLists, nsfwLevel, human, accuracy }, refetch);
            }}
            accuracy={accuracy}
            onAccuracyChange={(value) => {
              setAccuracy(value);
              updateBrowserPolicy({ accuracy: value }, { ...currentLists, nsfwLevel, human, accuracy }, refetch);
            }}
          />
        );
    }
  };

  return (
    <ConvexErrorBoundary>
      <div className="flex h-full flex-col">
        {/* Debug panel - only shown with ?debugPolicy=1 */}
        {showDebug && (
          <div className="p-3 border-b">
            <PolicyDebugCard deviceId={deviceId} />
          </div>
        )}
        
        <div className="flex flex-1 min-h-0">
          <DeviceSidebar
            title="Browser Management"
            items={filteredSidebarItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
          />
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto p-3">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </ConvexErrorBoundary>
  );
};
